import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import "./CategoryShowcaseSection.css";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import {
  fetchCategoryTree,
  fetchDealsRail,
} from "../../../../Redux/ecomCatalog/slice";
import { slugify } from "../CategoryListingTypes";

type ShowcaseCategory = {
  id: string;
  title: string;
  subtitle: string;
  image: string;
  route: string;
};

type ShowcaseSlide = {
  id: string;
  tag: string;
  title: string;
  highlight: string;
  description: string;
  image: string;
  brands: string[];
  route: string;
};

// Static gradient placeholder used when the tree endpoint returns no images.
// The tree endpoint (GET /catalog/categories/tree) returns {name, count} only —
// no images — so we synthesize a presentational image for the category strip.
const gradientFor = (name: string) => {
  const palette = [
    "linear-gradient(135deg, #1e3a5f, #3b6ea5)",
    "linear-gradient(135deg, #6b4226, #b5763c)",
    "linear-gradient(135deg, #2c5e2c, #5fa05f)",
    "linear-gradient(135deg, #5a1e3a, #b53d6b)",
    "linear-gradient(135deg, #4a3a6e, #7a5fa8)",
    "linear-gradient(135deg, #6e5a1e, #b59a3c)",
    "linear-gradient(135deg, #1e5a5a, #3ca0a0)",
    "linear-gradient(135deg, #5a2c1e, #b55a3c)",
    "linear-gradient(135deg, #1e3a5f, #5fa05f)",
  ];
  const hash = name.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return palette[hash % palette.length];
};

const CategoryShowcaseSection = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { tree, treeLoading, dealsRail, dealsRailLoading } = useAppSelector(
    (s) => s.ecomCatalog,
  );

  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  // Fetch the live category tree + deals rail on first mount.
  useEffect(() => {
    if (tree.length === 0) dispatch(fetchCategoryTree());
    if (dealsRail.length === 0)
      dispatch(fetchDealsRail({ limit: 4, sort: "discount" }));
  }, [dispatch, tree.length, dealsRail.length]);

  const slides: ShowcaseSlide[] = dealsRail.slice(0, 4).map((p) => ({
    id: `deal-${p._id}`,
    tag: "LIMITED TIME DEAL",
    title: p.name,
    highlight: `${p.featuredOffer?.deal?.discountPercent ?? 0}% off`,
    description: p.brand?.name ?? "Featured offer",
    image: p.mainImage,
    brands: [p.brand?.name].filter((b): b is string => Boolean(b)),
    route: `/DetailPage/${p._id}`,
  }));

  const categories: ShowcaseCategory[] = tree.slice(0, 9).map((node) => ({
    id: node.name,
    title: node.name,
    subtitle: `${node.count}+ products`,
    image: gradientFor(node.name),
    route: `/category/${slugify(node.name)}`,
  }));

  // Auto-rotate carousel — only when slides exist and not paused.
  useEffect(() => {
    if (paused || slides.length === 0) return;
    const timer = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  const handlePrevious = () => {
    if (slides.length === 0) return;
    setActiveSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleNext = () => {
    if (slides.length === 0) return;
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  // Loading skeletons while the tree/deals endpoints are still in flight.
  // Renders an empty section so the listing grid below is not blocked.
  if (treeLoading && categories.length === 0 && slides.length === 0) {
    return <section className="category-showcase" aria-hidden="true" />;
  }

  // If both endpoints return empty, hide the showcase entirely.
  if (categories.length === 0 && slides.length === 0) {
    return null;
  }

  const slide = slides[activeSlide];

  return (
    <section className="category-showcase">
      {categories.length > 0 && (
        <div className="category-showcase-category-strip">
          {categories.map((category) => (
            <button
              type="button"
              className="category-showcase-category-card"
              key={category.id}
              onClick={() => navigate(category.route)}
            >
              <span className="category-showcase-category-media">
                {category.image.startsWith("linear-gradient") ? (
                  <span
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                      background: category.image,
                    }}
                  />
                ) : (
                  <img
                    src={category.image}
                    alt={category.title}
                    loading="lazy"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display =
                        "none";
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.style.background = gradientFor(category.title);
                      }
                    }}
                  />
                )}
                <span className="category-showcase-category-glow" />
              </span>
              <span className="category-showcase-category-copy">
                <strong>{category.title}</strong>
                <small>{category.subtitle}</small>
              </span>
            </button>
          ))}
        </div>
      )}

      {slides.length > 0 && slide && (
        <div
          className="category-showcase-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="category-showcase-carousel-accent" />
          <button
            type="button"
            className="category-showcase-arrow category-showcase-arrow-left"
            onClick={handlePrevious}
            aria-label="Previous offer"
          >
            <ChevronLeft size={20} strokeWidth={2} />
          </button>
          <div className="category-showcase-hero">
            <button
              type="button"
              className="category-showcase-hero-media"
              onClick={() => navigate(slide.route)}
              aria-label={`Shop ${slide.title}`}
            >
              <img
                key={slide.image}
                src={slide.image}
                alt={slide.title}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
              <span className="category-showcase-image-shade" />
              <span className="category-showcase-image-label">
                Curated by Salar
              </span>
            </button>
            <div className="category-showcase-hero-content">
              <span className="category-showcase-tag">{slide.tag}</span>
              <h2>{slide.title}</h2>
              <strong className="category-showcase-highlight">
                {slide.highlight}
              </strong>
              <p>{slide.description}</p>
              {slide.brands.length > 0 && (
                <div className="category-showcase-brand-row">
                  {slide.brands.map((brand) => (
                    <span key={brand}>{brand}</span>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="category-showcase-shop-button"
                onClick={() => navigate(slide.route)}
              >
                Explore collection <ArrowUpRight size={16} strokeWidth={2} />
              </button>
            </div>
          </div>
          <button
            type="button"
            className="category-showcase-arrow category-showcase-arrow-right"
            onClick={handleNext}
            aria-label="Next offer"
          >
            <ChevronRight size={20} strokeWidth={2} />
          </button>
          <div className="category-showcase-progress">
            {slides.map((item, index) => (
              <button
                type="button"
                key={item.id}
                className={activeSlide === index ? "active" : ""}
                onClick={() => setActiveSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              >
                <span />
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default CategoryShowcaseSection;
